import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserAuthService } from '../UserAuth.service';
import { PreferencesService } from '../services/preferences.service';
import {
  ColorPalette,
  CompanyPreferences,
  DateRange,
  EffectivePreferences,
  Language,
  MeasurementSystem,
  StatusFilter,
  Theme,
  TimeIn3DGraph,
  UserPreferences,
} from '../services/preferences.types';

type ActiveTab = 'user' | 'company';

const PDF_SECTIONS = ['Curing', 'Zones', 'UserParameters', 'Graph3D', 'TempOverTime', 'Notes', 'Log'];
const PROJECT_COLUMNS = ['ProjectName', 'SerialNumber', 'LastTelemetryReceived', 'FiberType', 'Version', 'CustomerName'];
const AUTH_PROVIDERS = ['msal', 'google', 'email'];

@Component({
  selector: 'app-preferences',
  templateUrl: './preferences.component.html',
  styleUrls: ['./preferences.component.scss'],
})
export class PreferencesComponent implements OnInit, OnDestroy {
  activeTab: ActiveTab = 'user';
  isAdmin = false;
  isSuperAdmin = false;

  // ── User form state ───────────────────────────────────────────────────────
  lockedFields: string[] = [];

  language: Language = 'en';
  measurementSystem: MeasurementSystem = 'metric';
  theme: Theme = 'dark';
  rowsPerPage = 25;
  temp3DRangeMin = -10;
  temp3DRangeMax = 180;
  timeIn3DGraph: TimeIn3DGraph = 'relative';
  chartsColorPalette: ColorPalette = 'default';
  chartsAutoRefreshIntervalS = 30;
  projectListDefaultStatusFilter: StatusFilter = 'all';
  projectListDefaultDateRange: DateRange = 'all';
  exportsDefaultTemplate = 'Standard';
  notificationsEmailEnabled = true;
  notificationsRealtimeAlerts = true;
  notifyOnPreferenceOverwrite = true;
  wizardTemplateId = '';
  fiberTimer = 10;
  cureDefaultSheetId = '';

  userSaveStatus: 'idle' | 'saving' | 'success' | 'error' = 'idle';
  userErrorMessage = '';

  // ── Company form state ────────────────────────────────────────────────────
  companyName: string | null = null;

  // Localization
  cDefaultLanguage: Language = 'en';
  cLanguageLocked = false;
  cDefaultMeasurementSystem: MeasurementSystem = 'metric';
  cMeasurementSystemLocked = false;
  cTimeFormat = 'HH:mm:ss';
  cTimeFormatLocked = false;
  cDateFormat = 'yyyy-MM-dd';
  cDateFormatLocked = false;

  // Charts
  cDefault3DTempMin = -10;
  cDefault3DTempMax = 180;
  cTemp3DLocked = false;
  cDefaultChartsColorPalette: ColorPalette = 'default';
  cChartsColorPaletteLocked = false;
  cDefault3DTimeMode: TimeIn3DGraph = 'relative';
  cDefaultFiberTimer = 10;

  // Branding
  currentLogoUrl: string | null = null;
  selectedLogoFile: File | null = null;
  logoPreviewUrl: string | null = null;
  cPrimaryColor = '#1a73e8';
  cBrandingDescription = '';
  cExportReportFooterText = '';

  // Notifications
  cNotificationsEmailEnabled = true;
  cNotificationsEmailLocked = false;
  cNotificationsRealtimeAlerts = true;
  cNotificationsRealtimeLocked = false;

  // Security
  cSecuritySessionTimeoutMinutes = 60;
  cSecurityRequireMfa = false;

  // Subscriptions
  cHybridStreamingEnabled = true;

  companySaveStatus: 'idle' | 'saving' | 'success' | 'error' = 'idle';
  companyErrorMessage = '';
  logoUploadStatus: 'idle' | 'uploading' | 'success' | 'error' = 'idle';
  logoErrorMessage = '';

  // ── Options lists ─────────────────────────────────────────────────────────
  readonly languageOptions: { value: Language; label: string }[] = [
    { value: 'en', label: 'English' },
    { value: 'nl', label: 'Dutch' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'it', label: 'Italian' },
    { value: 'es', label: 'Spanish' },
  ];

  readonly colorPaletteOptions: { value: ColorPalette; label: string }[] = [
    { value: 'default',         label: 'Default' },
    { value: 'colorblind_safe', label: 'Colorblind Safe' },
    { value: 'high_contrast',   label: 'High Contrast' },
  ];

  readonly statusFilterOptions: { value: StatusFilter; label: string }[] = [
    { value: 'all',        label: 'All' },
    { value: 'live',       label: 'Live only' },
    { value: 'historical', label: 'Historical only' },
  ];

  readonly dateRangeOptions: { value: DateRange; label: string }[] = [
    { value: '7d',  label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: 'all', label: 'All time' },
  ];

  readonly pdfSectionOptions = PDF_SECTIONS;

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private preferencesService: PreferencesService,
    public userAuth: UserAuthService
  ) {}

  ngOnInit(): void {
    this.userAuth.isAdmin$.pipe(takeUntil(this._destroying$)).subscribe(a => this.isAdmin = a);
    this.userAuth.isSuperAdmin$.pipe(takeUntil(this._destroying$)).subscribe(sa => this.isSuperAdmin = sa);

    this.preferencesService.effective$
      .pipe(takeUntil(this._destroying$))
      .subscribe(e => {
        this.lockedFields = e.lockedFields ?? [];
        this.populateUserForm(e);
        this.currentLogoUrl = e.reportLogoUrl ?? e.brandLogoUrl;
        this.companyName = this.userAuth.getActiveCustomer()?.CustomerName ?? e.companyName;
      });

    this.userAuth.activeCustomer$.pipe(takeUntil(this._destroying$)).subscribe(c => {
      if (c?.CustomerName) this.companyName = c.CustomerName;
    });

    this.preferencesService.company$
      .pipe(takeUntil(this._destroying$))
      .subscribe(c => {
        this.populateCompanyForm(c);
        this.currentLogoUrl = c.reportLogoUrl ?? c.brandLogoUrl;
      });
  }

  ngOnDestroy(): void {
    if (this.logoPreviewUrl) URL.revokeObjectURL(this.logoPreviewUrl);
    this._destroying$.next();
    this._destroying$.complete();
  }

  selectTab(tab: ActiveTab): void { this.activeTab = tab; }

  isLocked(field: string): boolean {
    return this.lockedFields.includes(field);
  }

  // ── User submit ───────────────────────────────────────────────────────────

  submitUser(): void {
    this.userSaveStatus = 'saving';
    this.userErrorMessage = '';

    const patch: Partial<UserPreferences> = {
      language: this.language,
      measurementSystem: this.measurementSystem,
      theme: this.theme,
      rowsPerPage: this.rowsPerPage,
      temp3DRange: { min: this.temp3DRangeMin, max: this.temp3DRangeMax },
      timeIn3DGraph: this.timeIn3DGraph,
      chartsColorPalette: this.chartsColorPalette,
      chartsAutoRefreshIntervalS: this.chartsAutoRefreshIntervalS,
      projectListDefaultStatusFilter: this.projectListDefaultStatusFilter,
      projectListDefaultDateRange: this.projectListDefaultDateRange,
      exportsDefaultTemplate: this.exportsDefaultTemplate || null,
      notificationsEmailEnabled: this.notificationsEmailEnabled,
      notificationsRealtimeAlerts: this.notificationsRealtimeAlerts,
      notifyOnPreferenceOverwrite: this.notifyOnPreferenceOverwrite,
      wizardTemplateId: this.wizardTemplateId || null,
      fiberTimer: this.fiberTimer,
      cureDefaultSheetId: this.cureDefaultSheetId || null,
    };

    // Strip locked fields — backend rejects them anyway, but avoid the round-trip.
    (Object.keys(patch) as Array<keyof UserPreferences>).forEach(k => {
      if (this.isLocked(k)) delete patch[k];
    });

    this.preferencesService.saveUser(patch).subscribe({
      next: () => {
        this.userSaveStatus = 'success';
        setTimeout(() => (this.userSaveStatus = 'idle'), 3000);
      },
      error: (err) => {
        this.userSaveStatus = 'error';
        if (err?.status === 403 && err?.error?.lockedFields) {
          this.userErrorMessage = `These settings are locked by your company: ${err.error.lockedFields.join(', ')}`;
        } else {
          this.userErrorMessage = err?.error?.error || 'Failed to save preferences. Please try again.';
        }
      },
    });
  }

  // ── Company submit ────────────────────────────────────────────────────────

  submitCompany(): void {
    this.companySaveStatus = 'saving';
    this.companyErrorMessage = '';

    const patch: Partial<CompanyPreferences> = {
      defaultLanguage: this.cDefaultLanguage,
      languageLocked: this.cLanguageLocked,
      defaultMeasurementSystem: this.cDefaultMeasurementSystem,
      measurementSystemLocked: this.cMeasurementSystemLocked,
      timeFormat: this.cTimeFormat,
      timeFormatLocked: this.cTimeFormatLocked,
      dateFormat: this.cDateFormat,
      dateFormatLocked: this.cDateFormatLocked,
      default3DTempMin: this.cDefault3DTempMin,
      default3DTempMax: this.cDefault3DTempMax,
      temp3DLocked: this.cTemp3DLocked,
      defaultChartsColorPalette: this.cDefaultChartsColorPalette,
      chartsColorPaletteLocked: this.cChartsColorPaletteLocked,
      default3DTimeMode: this.cDefault3DTimeMode,
      defaultFiberTimer: this.cDefaultFiberTimer,
      primaryColor: this.cPrimaryColor,
      brandingDescription: this.cBrandingDescription,
      exportReportFooterText: this.cExportReportFooterText,
      notificationsEmailEnabled: this.cNotificationsEmailEnabled,
      notificationsEmailLocked: this.cNotificationsEmailLocked,
      notificationsRealtimeAlerts: this.cNotificationsRealtimeAlerts,
      notificationsRealtimeLocked: this.cNotificationsRealtimeLocked,
      securitySessionTimeoutMinutes: this.cSecuritySessionTimeoutMinutes,
      securityRequireMfa: this.cSecurityRequireMfa,
      hybridStreamingEnabled: this.cHybridStreamingEnabled,
    };

    this.preferencesService.saveCompany(patch).subscribe({
      next: () => {
        this.companySaveStatus = 'success';
        setTimeout(() => (this.companySaveStatus = 'idle'), 3000);
      },
      error: (err) => {
        this.companySaveStatus = 'error';
        this.companyErrorMessage = err?.error?.error || 'Failed to save company preferences.';
      },
    });
  }

  // ── Logo upload ───────────────────────────────────────────────────────────

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    this.logoErrorMessage = '';
    if (!file.type.startsWith('image/')) { this.logoErrorMessage = 'Please select an image file.'; return; }
    if (file.size > 2 * 1024 * 1024) { this.logoErrorMessage = 'Image must be smaller than 2 MB.'; return; }
    if (this.logoPreviewUrl) URL.revokeObjectURL(this.logoPreviewUrl);
    this.selectedLogoFile = file;
    this.logoPreviewUrl = URL.createObjectURL(file);
  }

  uploadLogo(): void {
    if (!this.selectedLogoFile) return;
    this.logoUploadStatus = 'uploading';
    this.logoErrorMessage = '';
    this.preferencesService.uploadLogo(this.selectedLogoFile).subscribe({
      next: (url) => {
        this.currentLogoUrl = url;
        this.logoPreviewUrl = null;
        this.selectedLogoFile = null;
        this.logoUploadStatus = 'success';
        setTimeout(() => (this.logoUploadStatus = 'idle'), 3000);
      },
      error: (err) => {
        this.logoUploadStatus = 'error';
        this.logoErrorMessage = err?.error?.error || 'Logo upload failed.';
      },
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private populateUserForm(e: EffectivePreferences): void {
    this.language                     = e.language;
    this.measurementSystem            = e.measurementSystem;
    this.theme                        = e.theme;
    this.rowsPerPage                  = e.rowsPerPage ?? 25;
    this.temp3DRangeMin               = e.temp3DRange?.min ?? -10;
    this.temp3DRangeMax               = e.temp3DRange?.max ?? 180;
    this.timeIn3DGraph                = e.timeIn3DGraph;
    this.chartsColorPalette           = e.chartsColorPalette ?? 'default';
    this.chartsAutoRefreshIntervalS   = e.chartsAutoRefreshIntervalS ?? 30;
    this.projectListDefaultStatusFilter = e.projectListDefaultStatusFilter ?? 'all';
    this.projectListDefaultDateRange  = e.projectListDefaultDateRange ?? 'all';
    this.exportsDefaultTemplate       = e.exportsDefaultTemplate ?? 'Standard';
    this.notificationsEmailEnabled    = e.notificationsEmailEnabled ?? true;
    this.notificationsRealtimeAlerts  = e.notificationsRealtimeAlerts ?? true;
    this.notifyOnPreferenceOverwrite  = e.notifyOnPreferenceOverwrite ?? true;
    this.wizardTemplateId             = e.wizardTemplateId ?? '';
    this.fiberTimer                   = e.fiberTimer ?? 10;
    this.cureDefaultSheetId           = e.cureDefaultSheetId ?? '';
  }

  private populateCompanyForm(c: CompanyPreferences): void {
    this.cDefaultLanguage             = c.defaultLanguage;
    this.cLanguageLocked              = c.languageLocked;
    this.cDefaultMeasurementSystem    = c.defaultMeasurementSystem;
    this.cMeasurementSystemLocked     = c.measurementSystemLocked;
    this.cTimeFormat                  = c.timeFormat;
    this.cTimeFormatLocked            = c.timeFormatLocked;
    this.cDateFormat                  = c.dateFormat;
    this.cDateFormatLocked            = c.dateFormatLocked;
    this.cDefault3DTempMin            = c.default3DTempMin;
    this.cDefault3DTempMax            = c.default3DTempMax;
    this.cTemp3DLocked                = c.temp3DLocked;
    this.cDefaultChartsColorPalette   = c.defaultChartsColorPalette ?? 'default';
    this.cChartsColorPaletteLocked    = c.chartsColorPaletteLocked;
    this.cDefault3DTimeMode           = c.default3DTimeMode;
    this.cDefaultFiberTimer           = c.defaultFiberTimer;
    this.cPrimaryColor                = c.primaryColor ?? '#1a73e8';
    this.cBrandingDescription         = c.brandingDescription ?? '';
    this.cExportReportFooterText      = c.exportReportFooterText ?? '';
    this.cNotificationsEmailEnabled   = c.notificationsEmailEnabled;
    this.cNotificationsEmailLocked    = c.notificationsEmailLocked;
    this.cNotificationsRealtimeAlerts = c.notificationsRealtimeAlerts;
    this.cNotificationsRealtimeLocked = c.notificationsRealtimeLocked;
    this.cSecuritySessionTimeoutMinutes = c.securitySessionTimeoutMinutes;
    this.cSecurityRequireMfa          = c.securityRequireMfa;
    this.cHybridStreamingEnabled      = c.hybridStreamingEnabled;
  }
}
